/**
 * Which back end serves a request while Fresh and Timescale run in parallel.
 *
 * Carried as the `dataSource` insight parameter, turned into a query parameter by
 * buildQuery, then consumed and stripped by resolveApiPath — see apiRoutes.ts.
 */
export type DataSource = 'fresh' | 'timescale'

export const DATA_SOURCE_VALUES: Record<DataSource, DataSource> = {
  fresh: 'fresh',
  timescale: 'timescale',
}

/** Fresh until an endpoint has proven parity, and what users without the permission get. */
export const DEFAULT_DATA_SOURCE: DataSource = 'fresh'

export const isDataSource = (value: unknown): value is DataSource =>
  value === DATA_SOURCE_VALUES.fresh || value === DATA_SOURCE_VALUES.timescale

/** The insight parameter / query key. Must match DATA_SOURCE_PARAM in apiRoutes.ts. */
export const DATA_SOURCE_KEY = 'dataSource'

/**
 * Insight slugs that actually have a Timescale implementation, by `InsightDefinition.slug`
 * (which is the route path minus the /insights prefix).
 *
 * Every other insight answers its top-level URL with an alias that runs the Fresh code, so
 * asking for Timescale would quietly return Fresh data — and two panels showing identical
 * numbers would look like proven parity when both had read the same database. Anything not
 * listed here refuses instead.
 *
 * Add a slug here in the same change that replaces its alias with a real implementation.
 * Forgetting fails safe: the insight reports "not available" while actually working, which is
 * irritating but not misleading.
 */
export const TIMESCALE_READY_INSIGHTS: ReadonlySet<string> = new Set([
  'asset/reefer-operation-history',
  'asset/location-history',
])

export const isTimescaleReady = (slug: string | undefined): boolean => !!slug && TIMESCALE_READY_INSIGHTS.has(slug)

/** Shown in the insight body when Timescale is selected on one that has no implementation. */
export const TIMESCALE_UNAVAILABLE_MESSAGE =
  'This insight does not read Timescale yet — it is still served from Fresh. ' +
  'Switch the data source back to Fresh to see data.'

/**
 * Whether the asset map can read Timescale.
 *
 * The map is driven by /assets/state/summary and /assets/state/:id, both of which are still
 * aliases running the Fresh code, so offering the choice would show Fresh data under a
 * Timescale label. Flip this to true in the change that converts those endpoints.
 */
export const ASSET_MAP_TIMESCALE_READY = false
